import { type InputHTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  label?: string;
  required?: boolean;
  hint?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, label, required, hint, id, type, ...props }, ref) => {
    const isAmount = type === 'number' || props.inputMode === 'decimal';
    const describedBy = error && id ? `${id}-error` : hint && id ? `${id}-hint` : undefined;
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={id} className="text-sm font-semibold text-text">
            {label}
            {required && (
              <span className="ml-1 text-danger" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}
        {error ? (
          <span id={id ? `${id}-error` : undefined} className="text-sm font-normal text-danger" role="alert">
            {error}
          </span>
        ) : hint ? (
          <span id={id ? `${id}-hint` : undefined} className="text-sm font-normal text-text-3">
            {hint}
          </span>
        ) : null}
        <input
          ref={ref}
          id={id}
          type={type}
          className={cn(
            'h-10 w-full rounded-md border border-border bg-surface px-3 text-sm font-normal text-text',
            'placeholder:text-text-3',
            'focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-primary',
            'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-text-3',
            error && 'border-danger',
            isAmount && 'font-mono',
            className
          )}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          {...props}
        />
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
export type { InputProps };
